/* eslint camelcase: 0 */
import { call, takeEvery, put, takeLatest } from 'redux-saga/effects';
import { combineReducers } from 'redux';
import { push } from 'react-router-redux';
import { SubmissionError, change as changeFormValue } from 'redux-form';
import PromiseCreator from 'promise-creator';
import dedent from 'dedent';
import toArray from 'lodash.toarray';
import get from 'lodash.get';
import { showAlert } from './alert';
import { showPromptErrorAction, openPromptAction, closePromptAction } from './prompt';
import request from '../api-utils/request';
import actionKey from '../utils/action-key';
import ASSESSMENTS from '../../constants/assessments';
import { psychGroups } from '../../constants/psych-groups';
import STUDY_LEVELS from '../../constants/study-levels';

export const POPULATE_WPP = 'employer-settings/POPULATE_WPP';
export const FETCH_POSITION_VALUES = 'employer-settings/FETCH_POSITION_VALUES';
export const SAVE_POSITION = 'employer-settings/SAVE_POSITION';
export const REMOVE_POSITION = 'employer-settings/REMOVE_POSITION';

const mapPosition = ({
    psychValues = [],
    assessmentValues = [],
    fitWeights = [],
    riskInteractionValues = [],
    idealScores = [],
    ...other
}) => ({
    ...other,
    psychValues: psychValues.reduce((acc, val) => {
        acc[val.psychCategoryID_FK] = val;
        return acc;
    }, {}),
    assessmentValues: assessmentValues.reduce((acc, val) => {
        acc[val.assessmentID_FK] = val;
        return acc;
    }, {}),
    fitWeights: fitWeights.reduce((acc, val) => {
        acc[val.assessmentID_FK] = val;
        return acc;
    }, {}),
    idealScores: idealScores.reduce((acc, val) => {
        acc[val.psychCategoryID_FK] = val;
        return acc;
    }, {}),
    riskInteractionValues: riskInteractionValues.reduce((acc, val) => {
        acc[val.interactionId] = val;
        return acc;
    }, {}),
});

export const positionValuesReducer = (state = {}, action) => {
    switch (action.type) {
        case FETCH_POSITION_VALUES:
            return {};
        case actionKey.success(FETCH_POSITION_VALUES):
            return mapPosition(action.payload.position);
        default:
            return state;
    }
};

export const loading = (state = null, action) => {
    switch (action.type) {
        case FETCH_POSITION_VALUES:
            return true;
        case actionKey.success(FETCH_POSITION_VALUES):
        case actionKey.failure(FETCH_POSITION_VALUES):
            return false;
        default:
            return state;
    }
};

export const error = (state = null, action) => {
    switch (action.type) {
        case FETCH_POSITION_VALUES:
        case actionKey.success(FETCH_POSITION_VALUES):
            return null;
        case actionKey.failure(FETCH_POSITION_VALUES):
            return action.error;
        default:
            return state;
    }
};

export const loadingPopulateWpp = (state = false, action) => {
    switch (action.type) {
        case actionKey.success(POPULATE_WPP):
        case actionKey.failure(POPULATE_WPP):
            return false;
        case POPULATE_WPP:
            return true;
        default:
            return state;
    }
};

export const populatedFrom = (state = null, action) => {
    switch (action.type) {
        case POPULATE_WPP:
            return action.positionId;
        default:
            return state;
    }
};

export default combineReducers({
    position: positionValuesReducer,
    error,
    loading,
    loadingPopulateWpp,
    populatedFrom,
});

export const fetchPositionValuesAction = positionId => ({
    type: FETCH_POSITION_VALUES,
    positionId,
});

export const savePositionAction = ({ reject, resolve, body, companyId }) => ({
    type: SAVE_POSITION,
    reject,
    resolve,
    body,
    companyId,
});

export const removePositionAction = ({ companyId, positionId, positionTitle }) => ({
    positionId,
    positionTitle,
    companyId,
    type: REMOVE_POSITION,
});

export const populateWPPAction = positionId => ({
    type: POPULATE_WPP,
    positionId,
});

export function* rootSaga() {
    yield [
        takeEvery(FETCH_POSITION_VALUES, fetchPositionValues),
        takeEvery(SAVE_POSITION, savePosition),
        takeEvery(REMOVE_POSITION, removePosition),
        takeLatest(POPULATE_WPP, populateWPP),
    ];
}

export function* savePosition({ type, body, companyId, resolve, reject }) {
    const psychValues = toArray(body.psychValues);
    const assessmentValues = toArray(body.assessmentValues);
    const weights = toArray(body.fitWeights);
    const idealScores = toArray(body.idealScores);
    const riskInteractionValues = body.riskInteractionValues;

    const position = {
        title: body.title,
        occupationID_ONET: body.occupationID_ONET,
        companyPositionID_PK: body.companyPositionID_PK,
        cutoffPercentage: body.cutoffPercentage,
        display: body.display,
        studyLevel: body.studyLevel,
    };

    const isNew = !body.companyPositionID_PK;

    let result;

    const data = {
        psychValues,
        assessmentValues,
        idealScores,
        position,
        weights,
        riskInteractionValues,
    };

    if (isNew) {
        result = yield call(request, {
            type,
            url: `/api/employer-settings/position/${companyId}`,
            options: {
                method: 'POST',
                body: data,
            },
        });
    } else {
        result = yield call(request, {
            type,
            url: `/api/employer-settings/${companyId}`,
            options: {
                method: 'PUT',
                body: data,
            },
        });
    }

    if (result.error) {
        if (reject) {
            reject(new SubmissionError({
                _error: result.error,
            }));
        }
        return result;
    }

    if (resolve) {
        resolve();
    }

    yield put(push(`/sites/${companyId}`));
    yield put(showAlert({
        title: isNew ? 'New Position Added' : 'Position updated',
        message: 'Your changes have been saved successfully.',
        type: 'success',
        button: 'Done',
    }));

    return result;
}

export function* fetchPositionValues({ type, positionId, companyId }) {
    yield call(request, {
        url: `/api/employer-settings/${positionId}`,
        type,
        companyId,
        positionId,
    });
}

export function* populateWPP({ type, positionId }) {
    const { payload } = yield call(request, {
        url: `/api/employer-settings/${positionId}`,
        type,
        positionId,
    });

    const position = payload.position;
    const form = 'company-position';
    if (position.studyLevel === STUDY_LEVELS.TALENT_ANALYTICS_FIT) {
        const wppValues = position.assessmentValues.find(value => (
            value.assessmentID_FK === ASSESSMENTS.WORKPLACE
        ));
        const min = get(wppValues, 'min', '');
        const max = get(wppValues, 'max', '');
        yield put(changeFormValue(form, `assessmentValues.${ASSESSMENTS.WORKPLACE}.min`, min));
        yield put(changeFormValue(form, `assessmentValues.${ASSESSMENTS.WORKPLACE}.max`, max));
        const psychValues = psychGroups.workplace.map(psychId => (
            position.psychValues.find(value => (
                value.psychCategoryID_FK === psychId
            ))
        ));
        const len = psychValues.length;
        for (let i = 0; i < len; i += 1) {
            const { mean, std, weight, psychCategoryID_FK } = psychValues[i] || {};

            yield put(changeFormValue(form, `psychValues.${psychCategoryID_FK}.mean`, mean));
            yield put(changeFormValue(form, `psychValues.${psychCategoryID_FK}.std`, std));
            yield put(changeFormValue(form, `psychValues.${psychCategoryID_FK}.weight`, weight));
        }
    } else if (position.studyLevel === STUDY_LEVELS.TALENT_INSIGHTS) {
        const idealScores = psychGroups.workplace.map(psychId => (
            position.idealScores.find(value => (
                value.psychCategoryID_FK === psychId
            ))
        ));

        const len = idealScores.length;
        for (let i = 0; i < len; i += 1) {
            const { score, primary, psychCategoryID_FK } = idealScores[i] || {};

            yield put(changeFormValue(form, `idealScores.${psychCategoryID_FK}.score`, score));
            yield put(changeFormValue(form, `idealScores.${psychCategoryID_FK}.primary`, primary));
        }
    }
}

export function* removePosition({ type, positionId, companyId, positionTitle }) {
    const { promise, resolve } = PromiseCreator();
    yield put(openPromptAction({
        resolve,
        title: 'Remove this Position?',
        message: dedent`
            This action will permanently remove the ${positionTitle} position from the system.
            Are you sure you want to proceed?
        `,
        okButton: 'Remove',
        primary: 'ok',
        showLoading: true,
    }));

    const upgrade = yield promise;
    if (upgrade) {
        const result = yield call(request, {
            type,
            url: `/api/employer-settings/position/${positionId}`,
            options: {
                method: 'DELETE',
            },
            positionId,
            companyId,
        });
        if (result.error) {
            yield put(showPromptErrorAction(result.error));
            yield removePosition({ type, positionId, positionTitle });
            return;
        }
    }
    yield put(closePromptAction());
}



// WEBPACK FOOTER //
// ./src/client/redux/employer-settings.js